import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Smartphone, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { PushDevice, PushStatus, WriteRejection } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { NOTIFICATIONS_KEY } from "@/components/notifications/queries";
import { ago } from "@/components/notifications/time";
import { LoadFailed } from "@/components/shell/EmptyState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  currentSubscription,
  deviceKey,
  notificationPermission,
  pushSupport,
  subscribeThisDevice,
  toDeviceInput,
  unsubscribeThisDevice,
  type PushSupport,
} from "@/lib/pwa/push";

const PUSH_KEY = [...NOTIFICATIONS_KEY, "push"] as const;

function describe(rejection: WriteRejection): string {
  switch (rejection.kind) {
    case "blocked":
    case "channel_rejected":
    case "conflict":
      return rejection.message;
    case "forbidden":
      return `${rejection.message} (requires ${rejection.permission})`;
    case "invalid":
      return rejection.fields
        ? Object.values(rejection.fields).join("; ")
        : rejection.message;
  }
}

/** What this browser itself says, which the server cannot know. */
interface Local {
  support: PushSupport;
  permission: NotificationPermission;
  /** The fingerprint of this browser's own subscription, if it has one. */
  key: string | null;
}

async function readLocal(): Promise<Local> {
  const subscription = await currentSubscription();
  return {
    support: pushSupport(),
    permission: notificationPermission(),
    key: subscription ? await deviceKey(subscription.endpoint) : null,
  };
}

function Notice({
  children,
  tone = "muted",
}: {
  children: React.ReactNode;
  tone?: "muted" | "warning";
}) {
  return (
    <p
      className={
        tone === "warning"
          ? "bg-destructive/10 text-destructive rounded-md px-3 py-2 text-sm"
          : "bg-muted text-muted-foreground rounded-md px-3 py-2 text-sm"
      }
    >
      {children}
    </p>
  );
}

function DeviceRow({
  device,
  isThis,
  busy,
  onRemove,
}: {
  device: PushDevice;
  isThis: boolean;
  busy: boolean;
  onRemove: () => void;
}) {
  return (
    <li className="flex items-center gap-3 py-2">
      <Smartphone
        className="text-muted-foreground size-4 shrink-0"
        aria-hidden
      />
      <div className="min-w-0 flex-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
          {device.label}
          {isThis ? <Badge variant="secondary">This device</Badge> : null}
        </p>
        <p className="text-muted-foreground text-xs">
          Added {ago(device.createdAt)}
          {device.lastUsedAt
            ? ` · last notification ${ago(device.lastUsedAt)}`
            : " · no notification yet"}
        </p>
      </div>
      {isThis ? null : (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={onRemove}
          aria-label={`Remove ${device.label}`}
        >
          <Trash2 aria-hidden />
          Remove
        </Button>
      )}
    </li>
  );
}

/**
 * Turning push on for this device, and seeing the others (E6, #446, slice 5).
 *
 * Everything the bell tells a person can also arrive as a notification on this
 * browser, even when EstelleERP is closed. Which kinds, per person, are the
 * "Phone push" boxes below; this card is about the *device*: asking the
 * browser's permission (only ever from the button), subscribing, telling the API,
 * and the reverse. On an iPhone the app has to be on the Home Screen first, and
 * the card says so rather than offer a button that cannot work.
 *
 * Opening the page also quietly re-registers this browser if it is already
 * subscribed and allowed — a browser can rotate its subscription, and the API
 * should always hold the current one.
 */
export function PushDeviceCard() {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const [local, setLocal] = useState<Local | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const status = useQuery({
    queryKey: PUSH_KEY,
    queryFn: () => data.notifications.pushStatus(),
  });

  const refresh = useCallback(async () => {
    setLocal(await readLocal());
    await queryClient.invalidateQueries({ queryKey: PUSH_KEY });
  }, [queryClient]);

  useEffect(() => {
    let cancelled = false;
    void readLocal().then((next) => {
      if (!cancelled) setLocal(next);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the API's copy current when the browser has rotated its subscription.
  const serverKeys = status.data?.devices.map((device) => device.key).join(",");
  useEffect(() => {
    if (!status.data?.configured || local === null) return;
    if (local.permission !== "granted" || local.key === null) return;
    if (status.data.devices.some((device) => device.key === local.key)) return;

    void (async () => {
      const subscription = await currentSubscription();
      const input = subscription ? toDeviceInput(subscription) : null;
      if (!input) return;
      const result = await data.notifications.registerPushDevice(input);
      if (result.ok)
        await queryClient.invalidateQueries({ queryKey: PUSH_KEY });
    })().catch(() => undefined);
    // `serverKeys` stands in for the device list, which is a new array on every fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data?.configured, serverKeys, local?.key, local?.permission]);

  const enable = useMutation({
    mutationFn: async (publicKey: string) => {
      const subscription = await subscribeThisDevice(publicKey);
      const input = toDeviceInput(subscription);
      if (!input) {
        await unsubscribeThisDevice();
        throw new Error("This browser did not give a usable push address.");
      }
      const result = await data.notifications.registerPushDevice(input);
      if (!result.ok) {
        await unsubscribeThisDevice();
        throw new Error(describe(result.rejection));
      }
    },
    onMutate: () => {
      setProblem(null);
      setMessage(null);
    },
    onSuccess: () => {
      setMessage("Push is on for this device.");
    },
    onError: (error) => {
      setProblem(error.message);
    },
    onSettled: refresh,
  });

  const disable = useMutation({
    mutationFn: async () => {
      const endpoint = await unsubscribeThisDevice();
      if (endpoint) {
        const result = await data.notifications.removePushDevice({ endpoint });
        if (!result.ok) throw new Error(describe(result.rejection));
      }
    },
    onMutate: () => {
      setProblem(null);
      setMessage(null);
    },
    onSuccess: () => {
      setMessage("Push is off for this device.");
    },
    onError: (error) => {
      setProblem(error.message);
    },
    onSettled: refresh,
  });

  const removeOther = useMutation({
    mutationFn: async (id: string) => {
      const result = await data.notifications.removePushDevice({ id });
      if (!result.ok) throw new Error(describe(result.rejection));
    },
    onMutate: () => {
      setProblem(null);
      setMessage(null);
    },
    onError: (error) => {
      setProblem(error.message);
    },
    onSettled: refresh,
  });

  const test = useMutation({
    mutationFn: async () => {
      const result = await data.notifications.sendPushTest();
      if (!result.ok) throw new Error(describe(result.rejection));
      return result.data.sent;
    },
    onMutate: () => {
      setProblem(null);
      setMessage(null);
    },
    onSuccess: (sent) => {
      setMessage(
        sent === 1
          ? "Test sent. It should appear in a moment."
          : `Test sent to ${String(sent)} devices. It should appear in a moment.`,
      );
    },
    onError: (error) => {
      setProblem(error.message);
    },
    onSettled: refresh,
  });

  const busy =
    enable.isPending ||
    disable.isPending ||
    removeOther.isPending ||
    test.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notifications on this device</CardTitle>
        <CardDescription>
          Get what the bell shows as a notification on your phone or computer,
          even when EstelleERP is closed. Quiet hours set by an administrator
          hold them until the quiet period ends.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {status.isPending || local === null ? (
          <Skeleton className="h-16 w-full" aria-hidden />
        ) : status.isError ? (
          <LoadFailed
            message={status.error.message}
            onRetry={() => {
              void status.refetch();
            }}
          />
        ) : (
          <CardBody
            status={status.data}
            local={local}
            busy={busy}
            onEnable={() => {
              if (status.data.publicKey) enable.mutate(status.data.publicKey);
            }}
            onDisable={() => {
              disable.mutate();
            }}
            onTest={() => {
              test.mutate();
            }}
            onRemove={(id) => {
              removeOther.mutate(id);
            }}
          />
        )}

        {message ? (
          <p role="status" className="text-sm">
            {message}
          </p>
        ) : null}
        {problem ? (
          <p role="alert" className="text-destructive text-sm">
            {problem}
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function CardBody({
  status,
  local,
  busy,
  onEnable,
  onDisable,
  onTest,
  onRemove,
}: {
  status: PushStatus;
  local: Local;
  busy: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onTest: () => void;
  onRemove: (id: string) => void;
}) {
  const here = local.key !== null;
  const registered = status.devices.some((device) => device.key === local.key);
  const on = here && registered;

  let control: React.ReactNode;
  if (!status.configured) {
    control = (
      <Notice>
        Push is not set up on this environment yet. An administrator needs to
        add the push keys before it can be turned on.
      </Notice>
    );
  } else if (local.support === "needs-install") {
    control = (
      <Notice>
        On an iPhone or iPad, add EstelleERP to your Home Screen first (Share,
        then Add to Home Screen), open it from there, and come back to this
        page.
      </Notice>
    );
  } else if (local.support === "unsupported") {
    control = <Notice>This browser cannot receive push notifications.</Notice>;
  } else if (local.permission === "denied" && !on) {
    control = (
      <Notice tone="warning">
        Notifications are blocked for this site in the browser. Allow them in
        the site settings, then reload this page.
      </Notice>
    );
  } else {
    control = (
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-2 text-sm font-medium">
          <BellRing
            className={on ? "text-primary-strong size-4" : "size-4"}
            aria-hidden
          />
          {on ? "On for this device" : "Off on this device"}
        </p>
        <div className="ml-auto flex flex-wrap gap-2">
          {on ? (
            <>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={onTest}
              >
                Send a test
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={onDisable}
              >
                Turn off
              </Button>
            </>
          ) : (
            <Button type="button" size="sm" disabled={busy} onClick={onEnable}>
              Turn on for this device
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {control}
      {status.devices.length > 0 ? (
        <div>
          <h3 className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            Your devices
          </h3>
          <ul className="divide-y">
            {status.devices.map((device) => (
              <DeviceRow
                key={device.id}
                device={device}
                isThis={device.key === local.key}
                busy={busy}
                onRemove={() => {
                  onRemove(device.id);
                }}
              />
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}
